/**
 * FolderDeleteDialog — confirms a folder deletion.
 * Picks the backend `mode`:
 *  - strict (default) — refuses if folder has modules or subfolders
 *  - orphan          — moves contents to root, then deletes the folder
 *  - cascade         — super_admin only — wipes everything underneath
 */
import { useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

export function FolderDeleteDialog({
  open, onOpenChange, folder, isSuperAdmin = false, onConfirm,
}) {
  const [mode, setMode] = useState('strict');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (chosenMode) => {
    setMode(chosenMode);
    setSubmitting(true);
    try {
      await onConfirm(chosenMode);
    } finally {
      setSubmitting(false);
    }
  };

  if (!folder) return null;

  return (
    <AlertDialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <AlertDialogContent data-testid="folder-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Delete "{folder.name}"?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>Choose how to handle the folder's contents:</p>
              <div className="space-y-2">
                <div className="rounded border p-3">
                  <div className="font-medium text-foreground">Only if empty</div>
                  <div className="text-muted-foreground text-xs">Refuses if any modules or subfolders exist. Safest.</div>
                </div>
                <div className="rounded border p-3">
                  <div className="font-medium text-foreground">Keep modules, move them to root</div>
                  <div className="text-muted-foreground text-xs">Modules become "Unfiled". Subfolders are reparented one level up.</div>
                </div>
                {isSuperAdmin && (
                  <div className="rounded border border-destructive/40 p-3 bg-destructive/5">
                    <div className="font-medium text-destructive">Cascade — delete folder + all modules</div>
                    <div className="text-xs text-muted-foreground">Wipes every module and subfolder underneath. Super admin only.</div>
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-col gap-2 items-stretch">
          <AlertDialogCancel disabled={submitting} data-testid="folder-delete-cancel">Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => handle('strict')}
            data-testid="folder-delete-strict"
          >
            {submitting && mode === 'strict' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Only if empty
          </Button>
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => handle('orphan')}
            data-testid="folder-delete-orphan"
          >
            {submitting && mode === 'orphan' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Move contents to root
          </Button>
          {isSuperAdmin && (
            <AlertDialogAction
              onClick={() => handle('cascade')}
              disabled={submitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="folder-delete-cascade"
            >
              {submitting && mode === 'cascade' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cascade delete everything
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
